import type { ReactNode } from 'react'
import Box, { type BoxProps } from '@mui/material/Box'
import Typography, { type TypographyProps } from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import { mergeSx } from 'lib/helpers/mergeSx/mergeSx'
import { IconButton } from 'settle-ui/components/IconButton/IconButton'
import { ModalContentPaddingX } from 'settle-ui/components/Modal/components/ModalContent/ModalContent'

export interface ModalHeaderProps extends Omit<BoxProps, 'title'> {
  title?: ReactNode
  titleProps?: TypographyProps<'h6'>
  description?: ReactNode
  descriptionProps?: TypographyProps<'p'>
  onClose?: () => void
}

export const ModalHeader: Settle.FC<ModalHeaderProps> = ({
  title,
  titleProps,
  description,
  descriptionProps,
  onClose,
  children,
  sx,
  ...props
}) => (
  <Box
    {...props}
    sx={mergeSx(
      {
        paddingTop: 1.5,
        paddingX: ModalContentPaddingX,
      },
      sx,
    )}
    data-testid="modal-header"
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 1.5,
      }}
    >
      <Box sx={{ alignSelf: 'center' }}>
        {title && (
          <Typography
            variant="h6"
            {...titleProps}
            data-testid="modal-header-title"
            sx={mergeSx({ fontWeight: 500 }, titleProps?.sx)}
          >
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            variant="body1"
            color="onSurfaceSecondary"
            {...descriptionProps}
            data-testid="modal-header-description"
          >
            {description}
          </Typography>
        )}
      </Box>
      {onClose && (
        <IconButton size="medium" onClick={onClose} data-testid="modal-header-close-button">
          <CloseIcon data-testid="CloseIcon" />
        </IconButton>
      )}
    </Box>
    {children}
  </Box>
)
